import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrator", sales_executive: "Sales Executive", sales_manager: "Sales Manager",
  design: "Design / Prepress", production_planner: "Production Planner", production_operator: "Production Operator",
  qc_inspector: "QC Inspector", store: "Store / Inventory", purchase: "Purchase Officer",
  accounts: "Accounts", hr: "HR",
};

export default function Profile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">My Profile</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Account details</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between border-b border-slate-100 pb-2">
              <dt className="text-slate-500">Name</dt><dd className="font-medium text-slate-800">{user?.name}</dd>
            </div>
            <div className="flex justify-between border-b border-slate-100 pb-2">
              <dt className="text-slate-500">Email</dt><dd className="font-medium text-slate-800">{user?.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Role</dt><dd className="font-medium text-slate-800">{ROLE_LABELS[user?.role] || user?.role}</dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Change password</h3>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Current password</Label>
              <Input id="cur" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conf">Confirm new password</Label>
              <Input id="conf" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={loading} data-testid="change-password-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
